import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { REPORT_CATEGORY_I18N_KEY, type RunnableReport } from "@/lib/reportCatalog";
import { formatNumber } from "@/lib/format";

const CATEGORY_TONE: Record<string, string> = {
  许可证: "bg-primary-100 text-primary-700",
  激活码: "bg-accent-100 text-accent-700",
  客户: "bg-secondary-100 text-secondary-900",
  审计: "bg-primary-100 text-primary-700",
  监控: "bg-accent-100 text-accent-800",
};

// 超大范围阈值：合计或单份报表预计行数达到该值时，在范围确认弹窗里醒目提醒，避免误触大范围导出。
const LARGE_EXPORT_ROWS = 5000;

interface BatchExportPanelProps {
  reports: RunnableReport[];
  loading: boolean;
  running: boolean;
  onRun: (reports: RunnableReport[]) => void;
  // 每份报表的预计行数，供导出前的范围确认弹窗估算范围。
  estimates: Record<string, number>;
}

// 批量导出面板：勾选多份报表一次性打包生成，并汇总为一条导出任务。
// 与「单份生成」共用同一批报表定义，避免重复维护。
export default function BatchExportPanel({ reports, loading, running, onRun, estimates }: BatchExportPanelProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  // 导出范围确认弹窗：点「生成」先弹出，列出选中报表与预计行数，确认后才真正导出。
  const [confirmOpen, setConfirmOpen] = useState(false);

  const catLabel = (category: string) =>
    REPORT_CATEGORY_I18N_KEY[category] ? t(REPORT_CATEGORY_I18N_KEY[category]) : category;

  const selectedReports = useMemo(
    () => reports.filter((report) => selected.includes(report.key)),
    [reports, selected],
  );
  const allSelected = reports.length > 0 && selected.length === reports.length;

  const estimatedTotal = useMemo(
    () => selectedReports.reduce((sum, report) => sum + (estimates[report.key] ?? 0), 0),
    [selectedReports, estimates],
  );

  // 是否需要醒目提醒：合计预计行数偏高，或任意一份报表单独就偏高。
  const isLargeTotal = estimatedTotal >= LARGE_EXPORT_ROWS;
  const hasLargeReport = selectedReports.some((report) => (estimates[report.key] ?? 0) >= LARGE_EXPORT_ROWS);

  const toggle = (key: string) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : reports.map((report) => report.key));
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-stack-line" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">{t("reports.batchTitle")}</h2>
            <p className="text-xs text-foreground-500">{t("reports.batchSubtitle")}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleAll}
          disabled={loading || reports.length === 0}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
            <i className={allSelected ? "ri-checkbox-multiple-line" : "ri-checkbox-multiple-blank-line"} />
          </span>
          {allSelected ? t("reports.batchClear") : t("reports.batchSelectAll")}
        </button>
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-2">
        {reports.map((report) => {
          const checked = selected.includes(report.key);
          return (
            <button
              key={report.key}
              type="button"
              disabled={loading || running}
              onClick={() => toggle(report.key)}
              className={`flex items-start gap-3 rounded-md border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                checked
                  ? "border-primary-400 bg-primary-100/60"
                  : "border-background-200 bg-background-50 hover:border-primary-300"
              }`}
            >
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded ${
                  checked ? "text-primary-700" : "text-foreground-400"
                }`}
              >
                <i className={checked ? "ri-checkbox-line" : "ri-checkbox-blank-line"} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground-950">{report.name}</span>
                  <span
                    className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      CATEGORY_TONE[report.category] ?? "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {catLabel(report.category)}
                  </span>
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-foreground-500">{report.description}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
        <p className="text-xs text-foreground-600">
          {t("reports.batchSelected", { count: selectedReports.length })}
        </p>
        <button
          type="button"
          disabled={loading || running || selectedReports.length === 0}
          onClick={() => setConfirmOpen(true)}
          className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={running ? "ri-loader-4-line animate-spin" : "ri-stack-line"} />
          </span>
          {running ? t("reports.batchRunning") : t("reports.batchGenerate", { count: selectedReports.length })}
        </button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-foreground-950/40 px-4">
          <div className="w-full max-w-lg rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-stack-line" />
              </span>
              <div>
                <p className="font-heading text-sm font-semibold text-foreground-950">{t("reports.batchConfirmTitle")}</p>
                <p className="text-[11px] text-foreground-500">
                  {t("reports.batchConfirmSub", { count: selectedReports.length })}
                </p>
              </div>
            </div>

            <ul className="mt-4 max-h-[300px] divide-y divide-background-200 overflow-y-auto rounded-md border border-background-200">
              {selectedReports.map((report) => {
                const reportRows = estimates[report.key] ?? 0;
                const reportLarge = reportRows >= LARGE_EXPORT_ROWS;
                return (
                  <li
                    key={report.key}
                    className={`flex items-center justify-between gap-3 px-3 py-2.5 ${reportLarge ? "bg-accent-50/70" : ""}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-foreground-950">{report.name}</p>
                      <p className="mt-0.5 text-[10px] text-foreground-500">{catLabel(report.category)}</p>
                    </div>
                    <span
                      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] ${
                        reportLarge ? "bg-accent-100 font-medium text-accent-800" : "bg-background-100 text-foreground-600"
                      }`}
                    >
                      {reportLarge && <i className="ri-alert-line" />}
                      {t("reports.batchConfirmEstimate", { rows: formatNumber(reportRows) })}
                    </span>
                  </li>
                );
              })}
            </ul>

            {(isLargeTotal || hasLargeReport) && (
              <div className="mt-3 flex items-start gap-2.5 rounded-md border border-accent-300 bg-accent-50 px-3 py-2.5">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-700">
                  <i className="ri-alert-line" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-accent-800">{t("reports.batchConfirmLargeTitle")}</p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-accent-700">
                    {t("reports.batchConfirmLargeBody", { rows: formatNumber(estimatedTotal) })}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between rounded-md bg-background-100 px-3 py-2">
              <span className="text-[11px] text-foreground-600">{t("reports.batchConfirmTotalLabel")}</span>
              <span className="font-heading text-sm font-semibold text-foreground-950">
                {t("reports.batchConfirmTotal", { rows: formatNumber(estimatedTotal) })}
              </span>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {t("reports.batchCancel")}
              </button>
              <button
                type="button"
                disabled={running}
                onClick={() => {
                  setConfirmOpen(false);
                  onRun(selectedReports);
                }}
                className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-check-line" />
                </span>
                {t("reports.batchConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}